import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Linking,
  SafeAreaView,
  Alert,
  Platform,
  ActivityIndicator,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';
import { fetchAllChessStats, calculateAverageRating, calculateWinRate } from '../services/chessAPIs';
import { sendFriendRequest, getFriendshipStatus, respondToFriendRequest, removeFriend, cancelFriendRequest, joinClub, leaveClub, isClubMember, getClubMemberCount, getUserById, getPostsByAuthor, listEvents, listTournaments } from '../services/database';
import NotificationService from '../services/NotificationService';
import { getRecentSocialPosts } from '../services/socialSyncService';
import { getProfilePictureUrl } from '../services/storage';

const UserDetailScreen = ({ route, navigation }) => {
  const { userId } = route.params;
  const { user: authUser, appUser: authAppUser } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [chessStats, setChessStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserAccountType, setCurrentUserAccountType] = useState(null);
  const [pastEvents, setPastEvents] = useState([]);
  const [pastTournaments, setPastTournaments] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [friendshipStatus, setFriendshipStatus] = useState({ status: 'NONE' });
  const [friendActionLoading, setFriendActionLoading] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [recentSocialPosts, setRecentSocialPosts] = useState([]);
  const [todayEvent, setTodayEvent] = useState(null);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  useEffect(() => {
    if (authAppUser?.id) {
      setCurrentUserDbId(authAppUser.id);
      setIsAdmin(authAppUser.isAdmin || false);
      setCurrentUserAccountType(authAppUser.accountType);
    } else {
      setCurrentUserDbId(null);
      setIsAdmin(false);
      setCurrentUserAccountType(null);
    }
  }, [authAppUser]);

  // Fetch friendship status when we have both user IDs (use auth id so we don't miss a cycle before currentUserDbId state updates)
  useEffect(() => {
    const me = authAppUser?.id ?? currentUserDbId;
    if (me && userId && me !== userId) {
      checkFriendshipStatus();
    }
  }, [currentUserDbId, userId, authAppUser?.id]);

  // Check club membership status when viewing a club
  useEffect(() => {
    if (currentUserDbId && userId && user?.accountType === 'CLUB') {
      checkMembershipStatus();
      fetchMemberCount();
    }
  }, [currentUserDbId, userId, user?.accountType]);

  // Re-fetch posts when membership status changes (to show/hide member-only posts)
  useEffect(() => {
    if (user?.accountType === 'CLUB' && userId) {
      fetchClubPosts(userId);
    }
  }, [isMember]);

  const checkMembershipStatus = async () => {
    try {
      const memberStatus = await isClubMember(userId, currentUserDbId);
      setIsMember(memberStatus);
    } catch (error) {
      console.error('Error checking membership status:', error);
    }
  };

  const fetchMemberCount = async () => {
    try {
      const count = await getClubMemberCount(userId);
      setMemberCount(count);
    } catch (error) {
      console.error('Error fetching member count:', error);
    }
  };

  const handleJoinClub = async () => {
    if (!currentUserDbId) {
      showToast('Please log in to join clubs', 'error');
      return;
    }

    setMembershipLoading(true);
    try {
      await joinClub(userId, currentUserDbId);
      setIsMember(true);
      setMemberCount(prev => prev + 1);
      showToast('Successfully joined the club!', 'success');
    } catch (error) {
      console.error('Error joining club:', error);
      if (error.message === 'Already a member of this club') {
        showToast('You are already a member of this club', 'info');
        setIsMember(true);
      } else if (error.message === 'Club owners cannot join their own club') {
        showToast('You cannot join your own club', 'info');
      } else {
        showToast('Failed to join club', 'error');
      }
    } finally {
      setMembershipLoading(false);
    }
  };

  const handleLeaveClub = async () => {
    const confirmLeave = Platform.OS === 'web'
      ? window.confirm('Are you sure you want to leave this club?')
      : await new Promise((resolve) => {
          Alert.alert(
            'Leave Club',
            'Are you sure you want to leave this club?',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Leave', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmLeave) return;

    setMembershipLoading(true);
    try {
      await leaveClub(userId, currentUserDbId);
      setIsMember(false);
      setMemberCount(prev => Math.max(0, prev - 1));
      showToast('You have left the club', 'info');
    } catch (error) {
      console.error('Error leaving club:', error);
      showToast('Failed to leave club', 'error');
    } finally {
      setMembershipLoading(false);
    }
  };

  const checkFriendshipStatus = async () => {
    const me = authAppUser?.id ?? currentUserDbId;
    if (!me || !userId) return;
    try {
      const status = await getFriendshipStatus(me, userId);
      setFriendshipStatus(status);
    } catch (error) {
      console.error('Error checking friendship status:', error);
    }
  };

  const handleAddFriend = async () => {
    const senderId = authAppUser?.id ?? currentUserDbId;
    if (!senderId) {
      showToast('Please log in to add friends', 'error');
      return;
    }

    setFriendActionLoading(true);
    try {
      const result = await sendFriendRequest(senderId, userId);
      if (result?.status === 'PENDING' && result?.receiver_id === userId) {
        NotificationService.notifyFriendRequest(userId, authAppUser?.username || authAppUser?.clubName || 'Someone');
      }
      if (result?.status === 'ACCEPTED') {
        showToast(`You are now friends with ${user?.username}!`, 'success');
      } else {
        showToast('Friend request sent!', 'success');
      }
      checkFriendshipStatus();
    } catch (error) {
      console.error('Error sending friend request:', error);
      showToast(error?.message || 'Failed to send friend request', 'error');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleAcceptFriend = async () => {
    setFriendActionLoading(true);
    try {
      await respondToFriendRequest(friendshipStatus.requestId, 'ACCEPTED');
      showToast(`You are now friends with ${user?.username}!`, 'success');
      checkFriendshipStatus();
    } catch (error) {
      console.error('Error accepting friend request:', error);
      showToast('Failed to accept request', 'error');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    setFriendActionLoading(true);
    try {
      await cancelFriendRequest(friendshipStatus.requestId);
      showToast('Friend request cancelled', 'info');
      checkFriendshipStatus();
    } catch (error) {
      console.error('Error cancelling friend request:', error);
      showToast('Failed to cancel request', 'error');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleRemoveFriend = async () => {
    const confirmRemove = Platform.OS === 'web'
      ? window.confirm(`Remove ${user?.username} from friends?`)
      : await new Promise((resolve) => {
          Alert.alert(
            'Remove Friend',
            `Remove ${user?.username} from friends?`,
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Remove', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmRemove) return;

    setFriendActionLoading(true);
    try {
      const me = authAppUser?.id ?? currentUserDbId;
      if (!me) return;
      await removeFriend(me, userId);
      showToast(`Removed ${user?.username} from friends`, 'info');
      checkFriendshipStatus();
    } catch (error) {
      console.error('Error removing friend:', error);
      showToast('Failed to remove friend', 'error');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const renderFriendButton = () => {
    const me = authAppUser?.id ?? currentUserDbId;
    // Don't show friend button for own profile or clubs
    if (me === userId || user?.accountType === 'CLUB') {
      return null;
    }

    const buttonContent = () => {
      if (friendActionLoading) {
        return <ActivityIndicator size="small" color="white" />;
      }

      switch (friendshipStatus.status) {
        case 'ACCEPTED':
          return (
            <>
              <Icon name="check-circle" size={20} color={isDarkMode ? '#81C784' : '#2E7D32'} />
              <Text style={[styles.friendsButtonText, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>Friends</Text>
            </>
          );
        case 'PENDING':
          if (friendshipStatus.isSender) {
            return (
              <>
                <Icon name="hourglass-empty" size={20} color={colors.textSecondary} />
                <Text style={[styles.friendButtonText, { color: colors.textSecondary }]}>Request Pending</Text>
              </>
            );
          } else {
            return (
              <>
                <Icon name="person-add" size={20} color="white" />
                <Text style={styles.friendButtonText}>Accept Request</Text>
              </>
            );
          }
        default:
          return (
            <>
              <Icon name="person-add" size={20} color="white" />
              <Text style={styles.friendButtonText}>Add Friend</Text>
            </>
          );
      }
    };

    const handlePress = () => {
      switch (friendshipStatus.status) {
        case 'ACCEPTED':
          handleRemoveFriend();
          break;
        case 'PENDING':
          if (friendshipStatus.isSender) {
            handleCancelRequest();
          } else {
            handleAcceptFriend();
          }
          break;
        default:
          handleAddFriend();
          break;
      }
    };

    const buttonStyle = friendshipStatus.status === 'ACCEPTED'
      ? [styles.friendsButton, { borderColor: isDarkMode ? '#81C784' : '#2E7D32' }] // Already friends - outline style
      : friendshipStatus.status === 'PENDING' && friendshipStatus.isSender
      ? [styles.pendingButton, { backgroundColor: isDarkMode ? colors.backgroundDark : '#e0e0e0' }] // Request sent - gray style
      : [styles.addFriendButton, { backgroundColor: colors.primary }]; // Add friend or accept - primary style

    return (
      <TouchableOpacity 
        style={[
          ...buttonStyle,
          friendActionLoading && styles.buttonDisabled,
        ]}
        onPress={handlePress}
        disabled={friendActionLoading}
      >
        {buttonContent()}
      </TouchableOpacity>
    );
  };

  const fetchUserDetails = async () => {
    try {
      setLoading(true);

      if (!userId) {
        console.error('No userId provided');
        showToast('User ID is required', 'error');
        setLoading(false);
        return;
      }

      console.log('📥 Fetching user details for userId:', userId);

      // Fetch user data from Supabase
      const userDataRaw = await getUserById(userId);

      if (!userDataRaw) {
        console.error('User not found in database');
        showToast('User not found', 'error');
        setLoading(false);
        return;
      }

      // Convert snake_case to camelCase for UI
      const userData = {
        id: userDataRaw.id,
        username: userDataRaw.username,
        email: userDataRaw.email,
        accountType: userDataRaw.account_type,
        profilePictureUrl: userDataRaw.profile_picture_url,
        rating: userDataRaw.rating,
        gamesPlayedInApp: userDataRaw.games_played_in_app,
        chesscomUsername: userDataRaw.chesscom_username,
        lichessUsername: userDataRaw.lichess_username,
        clubName: userDataRaw.club_name,
        address: userDataRaw.address,
        about: userDataRaw.about,
        website: userDataRaw.website,
        schedule: userDataRaw.schedule,
        claimedBy: userDataRaw.claimed_by,
        tier: userDataRaw.tier,
        city: userDataRaw.city,
        statusMessage: userDataRaw.status_message,
        isOnline: userDataRaw.is_online,
        lastOnlineAt: userDataRaw.last_online_at,
        chesscomBlitz: userDataRaw.chesscom_blitz,
        chesscomRapid: userDataRaw.chesscom_rapid,
        chesscomClassical: userDataRaw.chesscom_classical,
        lichessBlitz: userDataRaw.lichess_blitz,
        lichessRapid: userDataRaw.lichess_rapid,
        lichessClassical: userDataRaw.lichess_classical,
      };

      setUser(userData);

      // Get profile picture URL if exists
      if (userData.profilePictureUrl) {
        const url = getProfilePictureUrl(userData.profilePictureUrl, 'large');
        setProfileImageUrl(url);
      }

      // Fetch chess stats for players
      if (userData.accountType === 'PLAYER' && (userData.chesscomUsername || userData.lichessUsername)) {
        const stats = await fetchAllChessStats(userData.chesscomUsername, userData.lichessUsername);
        setChessStats(stats);
      }

      if (userData.accountType === 'CLUB') {
        await Promise.all([
          fetchClubPosts(userId),
          fetchClubEvents(userId),
          fetchClubTournaments(userId),
          fetchRecentSocialPosts(userId),
        ]);
      }
    } catch (error) {
      console.error('❌ Error fetching user details:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        userId,
      });
      showToast('Failed to load user profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubEvents = async (clubId) => {
    try {
      const allEvents = await listEvents({ clubId, limit: 20 });
      
      const mapped = (allEvents || []).map(e => ({
        ...e,
        eventDate: e.event_date,
        eventTime: e.event_time,
        eventType: e.event_type,
        isPaid: e.is_paid,
        rsvpCount: e.rsvps?.length || 0,
      }));

      const now = new Date();
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(now);
      todayEnd.setHours(23, 59, 59, 999);

      const upcoming = mapped.filter(e => {
        const endDate = e.end_time ? new Date(e.end_time) : new Date(new Date(e.eventDate).getTime() + 4 * 3600000);
        return endDate >= todayStart;
      });
      const past = mapped.filter(e => {
        const endDate = e.end_time ? new Date(e.end_time) : new Date(new Date(e.eventDate).getTime() + 4 * 3600000);
        return endDate < todayStart;
      });

      const liveToday = upcoming.find(e => {
        const eventDate = new Date(e.eventDate);
        return eventDate >= todayStart && eventDate <= todayEnd;
      });
      setTodayEvent(liveToday || null);
      
      setUpcomingEvents(upcoming.slice(0, 5));
      setPastEvents(past.slice(0, 5));
    } catch (error) {
      console.error('Error fetching club events:', error);
    }
  };

  const fetchRecentSocialPosts = async (clubId) => {
    try {
      const { posts } = await getRecentSocialPosts(clubId, 5);
      setRecentSocialPosts(posts || []);
    } catch (error) {
      console.log('No social posts or error:', error.message);
      setRecentSocialPosts([]);
    }
  };

  const fetchClubTournaments = async (clubId) => {
    try {
      const allTournaments = await listTournaments({ clubId });
      
      // Map snake_case to camelCase and filter for completed
      const mapped = (allTournaments || []).map(t => ({
        ...t,
        totalRounds: t.total_rounds,
        currentRound: t.current_round,
        startTime: t.start_time,
      }));
      
      const completed = mapped.filter(t => t.status === 'COMPLETED');
      setPastTournaments(completed.slice(0, 5));
    } catch (error) {
      console.error('Error fetching club tournaments:', error);
    }
  };

  const fetchClubPosts = async (authorId) => {
    try {
      // Fetch posts using Supabase
      const postsData = await getPostsByAuthor(authorId);
      
      // Convert to camelCase and filter member-only posts
      const isOwner = user?.claimedBy === currentUserDbId;
      const allPosts = postsData.map(p => ({
        id: p.id,
        content: p.content,
        createdAt: p.created_at,
        isMemberOnly: p.is_member_only || false,
      }));
      
      // Filter posts: show public posts to everyone, member-only posts only to members or club owner
      const filteredPosts = allPosts.filter(post => {
        if (!post.isMemberOnly) return true; // Public post
        return isMember || isOwner; // Member-only: only show to members or owner
      });
      
      setPosts(filteredPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleSendMessage = () => {
    // Navigate to Chat tab with conversation params
    navigation.navigate('Chat', { 
      openUserId: userId, 
      openUser: user 
    });
  };

  const openWebsite = (url) => {
    if (url) {
      Linking.openURL(url.startsWith('http') ? url : `https://${url}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Dynamic styles based on theme
  const dynamicStyles = {
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background,
      padding: 20,
    },
    header: {
      backgroundColor: colors.surface,
      padding: 20,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      marginTop: 12,
      marginHorizontal: 16,
    },
    statCard: {
      flex: 1,
      backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9',
      padding: 16,
      borderRadius: 12,
      alignItems: 'center',
    },
    platformStats: {
      marginTop: 16,
      padding: 16,
      backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9',
      borderRadius: 12,
    },
    overallStats: {
      marginTop: 16,
      padding: 16,
      backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.2)' : '#E8F5E9',
      borderRadius: 12,
      alignItems: 'center',
    },
    statusContainer: {
      marginTop: 10,
      paddingHorizontal: 16,
      paddingVertical: 8,
      backgroundColor: isDarkMode ? colors.backgroundDark : '#f5f5f5',
      borderRadius: 8,
      maxWidth: '80%',
    },
    accountTypeBadge: {
      backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.2)' : '#E8F5E9',
      paddingHorizontal: 16,
      paddingVertical: 6,
      borderRadius: 20,
      marginTop: 8,
    },
    profileImagePlaceholder: {
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: colors.backgroundDark,
      alignItems: 'center',
      justifyContent: 'center',
    },
    onlineIndicator: {
      position: 'absolute',
      bottom: 8,
      right: 8,
      width: 22,
      height: 22,
      borderRadius: 11,
      borderWidth: 3,
      borderColor: colors.surface,
    },
  };

  if (loading) {
    return (
      <View style={dynamicStyles.loadingContainer}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  if (!user) {
    return (
      <View style={dynamicStyles.errorContainer}>
        <Icon name="error-outline" size={48} color={colors.textSecondary} />
        <Text style={[styles.errorText, { color: colors.text }]}>User not found</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={dynamicStyles.container}>
      <ScrollView>
        {/* Header */}
        <View style={dynamicStyles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Icon name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
          <View style={styles.profileImageContainer}>
            {profileImageUrl ? (
              <Image source={{ uri: profileImageUrl }} style={styles.profileImage} />
            ) : (
              <View style={dynamicStyles.profileImagePlaceholder}>
                <Icon name="person" size={60} color={colors.textSecondary} />
              </View>
            )}
            {/* Online Status Indicator */}
            <View style={[dynamicStyles.onlineIndicator, { backgroundColor: user.isOnline ? '#4CAF50' : '#9E9E9E' }]} />
          </View>
          <Text style={[styles.username, { color: colors.text }]}>
            {user.accountType === 'CLUB' ? user.clubName : user.username}
          </Text>
          
          {/* Online Status Text */}
          <View style={styles.onlineStatusContainer}>
            <View style={[styles.onlineStatusDot, { backgroundColor: user.isOnline ? '#4CAF50' : '#9E9E9E' }]} />
            <Text style={[styles.onlineStatusText, { color: colors.textSecondary }]}>
              {user.isOnline ? 'Online Now' : 'Offline'}
            </Text>
          </View>
          
          <View style={dynamicStyles.accountTypeBadge}>
            <Text style={[styles.accountTypeText, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>
              {user.accountType === 'CLUB' ? '🏛️ Club' : '♟️ Player'}
            </Text>
          </View>
          
          {/* City */}
          {user.city && (
            <View style={styles.cityContainer}>
              <Icon name="location-city" size={16} color={colors.textSecondary} />
              <Text style={[styles.cityText, { color: colors.textSecondary }]}>{user.city}</Text>
            </View>
          )}
          
          {/* Status Message */}
          {user.statusMessage && (
            <View style={dynamicStyles.statusContainer}>
              <Text style={[styles.statusText, { color: colors.textSecondary }]}>"{user.statusMessage}"</Text>
            </View>
          )}
        </View>

        {/* Player Profile */}
        {user.accountType === 'PLAYER' && (
          <View style={dynamicStyles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>Player Stats</Text>
            
            <View style={styles.statsGrid}>
              <View style={dynamicStyles.statCard}>
                <Text style={[styles.statValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{user.rating || 1200}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>In-App Rating</Text>
              </View>
              <View style={dynamicStyles.statCard}>
                <Text style={[styles.statValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{user.gamesPlayedInApp || 0}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Games Played</Text>
              </View>
            </View>

            {chessStats && (
              <>
                {chessStats.chesscom && (
                  <View style={dynamicStyles.platformStats}>
                    <Text style={[styles.platformTitle, { color: colors.text }]}>Chess.com</Text>
                    <View style={styles.ratingsRow}>
                      {chessStats.chesscom.ratings.rapid && (
                        <View style={styles.ratingItem}>
                          <Text style={[styles.ratingLabel, { color: colors.textSecondary }]}>Rapid</Text>
                          <Text style={[styles.ratingValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{chessStats.chesscom.ratings.rapid}</Text>
                        </View>
                      )}
                      {chessStats.chesscom.ratings.blitz && (
                        <View style={styles.ratingItem}>
                          <Text style={[styles.ratingLabel, { color: colors.textSecondary }]}>Blitz</Text>
                          <Text style={[styles.ratingValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{chessStats.chesscom.ratings.blitz}</Text>
                        </View>
                      )}
                      {chessStats.chesscom.ratings.bullet && (
                        <View style={styles.ratingItem}>
                          <Text style={[styles.ratingLabel, { color: colors.textSecondary }]}>Bullet</Text>
                          <Text style={[styles.ratingValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{chessStats.chesscom.ratings.bullet}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                )}

                {chessStats.lichess && (
                  <View style={dynamicStyles.platformStats}>
                    <Text style={[styles.platformTitle, { color: colors.text }]}>Lichess</Text>
                    <View style={styles.ratingsRow}>
                      {chessStats.lichess.ratings.rapid && (
                        <View style={styles.ratingItem}>
                          <Text style={[styles.ratingLabel, { color: colors.textSecondary }]}>Rapid</Text>
                          <Text style={[styles.ratingValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{chessStats.lichess.ratings.rapid}</Text>
                        </View>
                      )}
                      {chessStats.lichess.ratings.blitz && (
                        <View style={styles.ratingItem}>
                          <Text style={[styles.ratingLabel, { color: colors.textSecondary }]}>Blitz</Text>
                          <Text style={[styles.ratingValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{chessStats.lichess.ratings.blitz}</Text>
                        </View>
                      )}
                      {chessStats.lichess.ratings.bullet && (
                        <View style={styles.ratingItem}>
                          <Text style={[styles.ratingLabel, { color: colors.textSecondary }]}>Bullet</Text>
                          <Text style={[styles.ratingValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{chessStats.lichess.ratings.bullet}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                )}

                <View style={dynamicStyles.overallStats}>
                  <Text style={[styles.overallLabel, { color: colors.textSecondary }]}>Average Rating</Text>
                  <Text style={[styles.overallValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{calculateAverageRating(chessStats) || 'N/A'}</Text>
                  <Text style={[styles.overallLabel, { color: colors.textSecondary }]}>Win Rate</Text>
                  <Text style={[styles.overallValue, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>{calculateWinRate(chessStats)}%</Text>
                </View>
              </>
            )}
          </View>
        )}

        {/* Club Profile */}
        {user.accountType === 'CLUB' && (
          <>
            {/* 1. Live Today */}
            {todayEvent && (
              <TouchableOpacity
                style={[dynamicStyles.section, styles.liveTodaySection]}
                onPress={() => navigation.navigate('EventDiscovery', { eventId: todayEvent.id })}
                activeOpacity={0.8}
              >
                <View style={styles.liveTodayHeader}>
                  <View style={styles.liveDot} />
                  <Text style={styles.liveTodayLabel}>Live Today</Text>
                </View>
                <Text style={[styles.liveTodayTitle, { color: colors.text }]}>
                  {todayEvent.title}
                  {todayEvent.eventTime ? ` – ${(() => {
                    const [h, m] = todayEvent.eventTime.split(':').map(Number);
                    const ampm = h >= 12 ? 'pm' : 'am';
                    const hour12 = h % 12 || 12;
                    return `${hour12}${m ? `:${String(m).padStart(2, '0')}` : ''}${ampm}`;
                  })()}` : ''}
                </Text>
                {todayEvent.rsvpCount > 0 && (
                  <Text style={[styles.liveTodayRsvp, { color: colors.textSecondary }]}>
                    {todayEvent.rsvpCount} {todayEvent.rsvpCount === 1 ? 'person' : 'people'} going
                  </Text>
                )}
              </TouchableOpacity>
            )}

            {/* 2. Upcoming Events */}
            {upcomingEvents.length > 0 && (
              <View style={dynamicStyles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Upcoming Events</Text>
                {upcomingEvents.map((event) => (
                  <TouchableOpacity
                    key={event.id}
                    style={[styles.eventCard, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9' }]}
                    onPress={() => navigation.navigate('EventDiscovery', { eventId: event.id })}
                  >
                    <View style={styles.eventCardContent}>
                      <View style={styles.eventDateBadge}>
                        <Text style={[styles.eventDateDay, { color: colors.text }]}>
                          {new Date(event.eventDate).getDate()}
                        </Text>
                        <Text style={[styles.eventDateMonth, { color: colors.textSecondary }]}>
                          {new Date(event.eventDate).toLocaleString('default', { month: 'short' })}
                        </Text>
                      </View>
                      <View style={styles.eventInfo}>
                        <Text style={[styles.eventTitle, { color: colors.text }]}>{event.title}</Text>
                        <View style={styles.eventMeta}>
                          <View style={styles.eventTypeTag}>
                            <Text style={[styles.eventTypeText, { color: colors.text }]}>{event.eventType}</Text>
                          </View>
                          <Text style={[styles.eventRsvpCount, { color: colors.textSecondary }]}>
                            {event.rsvpCount || 0} RSVPs
                          </Text>
                        </View>
                      </View>
                    </View>
                    <Icon name="chevron-right" size={20} color={colors.textSecondary} />
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {/* 3. Recent Instagram Posts */}
            {recentSocialPosts.length > 0 && (
              <View style={dynamicStyles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Recent Activity</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.socialPostsScroll}>
                    {recentSocialPosts.map((post) => (
                      <TouchableOpacity
                        key={post.id}
                        style={[styles.socialPostCard, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9' }]}
                        onPress={() => post.permalink && Linking.openURL(post.permalink)}
                        activeOpacity={0.7}
                      >
                        {(() => {
                          let payload = post.raw_payload || {};
                          if (typeof payload === 'string') {
                            try {
                              payload = JSON.parse(payload);
                            } catch {
                              payload = {};
                            }
                          }
                          const imageUrl =
                            (payload && typeof payload.thumbnail_url === 'string' && payload.thumbnail_url) ||
                            post.media_url;
                          if (imageUrl) {
                            return (
                              <Image
                                source={{ uri: imageUrl }}
                                style={styles.socialPostImage}
                                resizeMode="cover"
                              />
                            );
                          }
                          return (
                            <View style={[styles.socialPostImage, { backgroundColor: colors.backgroundDark, alignItems: 'center', justifyContent: 'center' }]}>
                              <Icon name="image" size={28} color={colors.textSecondary} />
                            </View>
                          );
                        })()}
                        {post.caption && (
                          <Text style={[styles.socialPostCaption, { color: colors.textSecondary }]} numberOfLines={2}>
                            {post.caption}
                          </Text>
                        )}
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
              </View>
            )}

            {/* 4. Members */}
            <View style={dynamicStyles.section}>
              <View style={styles.memberCountRow}>
                <View style={styles.memberCountInfo}>
                  <Icon name="people" size={24} color={colors.primary} />
                  <View style={styles.memberCountTextContainer}>
                    <Text style={[styles.memberCountValue, { color: colors.text }]}>{memberCount}</Text>
                    <Text style={[styles.memberCountLabel, { color: colors.textSecondary }]}>
                      {memberCount === 1 ? 'Member' : 'Members'}
                    </Text>
                  </View>
                </View>
                {isMember && (
                  <View style={styles.memberBadge}>
                    <Icon name="check-circle" size={16} color="#4CAF50" />
                    <Text style={[styles.memberBadgeText, { color: colors.text }]}>You're a member</Text>
                  </View>
                )}
              </View>
            </View>

            {/* About */}
            {user.about && (
              <View style={dynamicStyles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>About</Text>
                <Text style={[styles.aboutText, { color: colors.textSecondary }]}>{user.about}</Text>
              </View>
            )}

            {user.address && (
              <View style={dynamicStyles.section}>
                <View style={styles.infoRow}>
                  <Icon name="location-on" size={20} color={colors.primary} />
                  <Text style={[styles.infoText, { color: colors.text }]}>{user.address}</Text>
                </View>
              </View>
            )}

            {user.website && (
              <View style={dynamicStyles.section}>
                <TouchableOpacity onPress={() => openWebsite(user.website)} style={styles.websiteButton}>
                  <Icon name="language" size={20} color={colors.primary} />
                  <Text style={[styles.websiteText, { color: colors.primary }]}>{user.website}</Text>
                </TouchableOpacity>
              </View>
            )}

            {user.schedule && (
              <View style={dynamicStyles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Schedule</Text>
                <Text style={[styles.scheduleText, { color: colors.textSecondary }]}>{user.schedule}</Text>
              </View>
            )}

            {/* Announcements */}
            {posts.length > 0 && (
              <View style={dynamicStyles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Announcements</Text>
                {posts.map((post) => (
                  <View key={post.id} style={[styles.postCard, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9' }]}>
                    {post.isMemberOnly && (
                      <View style={styles.memberOnlyBadge}>
                        <Icon name="lock" size={12} color={colors.primary} />
                        <Text style={[styles.memberOnlyBadgeText, { color: colors.primary }]}>Members only</Text>
                      </View>
                    )}
                    <Text style={[styles.postContent, { color: colors.text }]}>{post.content}</Text>
                    <Text style={[styles.postDate, { color: colors.textSecondary }]}>{formatDate(post.createdAt)}</Text>
                  </View>
                ))}
              </View>
            )}

            {/* Past Events & Tournaments */}
            {(pastEvents.length > 0 || pastTournaments.length > 0) && (
              <View style={dynamicStyles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Past Events & Tournaments</Text>
                
                {pastTournaments.map((tournament) => (
                  <TouchableOpacity
                    key={tournament.id}
                    style={styles.historyCard}
                    onPress={() => navigation.navigate('TournamentDetail', { tournamentId: tournament.id })}
                  >
                    <View style={[styles.historyIconContainer, { backgroundColor: isDarkMode ? 'rgba(255, 179, 0, 0.2)' : '#FFF3E0' }]}>
                      <Icon name="emoji-events" size={24} color="#FFB300" />
                    </View>
                    <View style={styles.historyInfo}>
                      <Text style={[styles.historyTitle, { color: colors.text }]}>{tournament.name}</Text>
                      <View style={styles.historyMeta}>
                        <Text style={[styles.historyMetaText, { color: colors.textSecondary }]}>
                          {tournament.type.replace('_', ' ')} • {tournament.totalRounds || '?'} rounds
                        </Text>
                      </View>
                    </View>
                    <View style={styles.completedBadge}>
                      <Text style={[styles.completedBadgeText, { color: colors.textSecondary }]}>Completed</Text>
                    </View>
                  </TouchableOpacity>
                ))}

                {pastEvents.map((event) => (
                  <TouchableOpacity
                    key={event.id}
                    style={[styles.historyCard, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9' }]}
                    onPress={() => navigation.navigate('EventDiscovery', { eventId: event.id })}
                  >
                    <View style={[styles.historyIconContainer, { backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.2)' : '#E3F2FD' }]}>
                      <Icon name="event" size={24} color={colors.primary} />
                    </View>
                    <View style={styles.historyInfo}>
                      <Text style={[styles.historyTitle, { color: colors.text }]}>{event.title}</Text>
                      <Text style={[styles.historyDate, { color: colors.textSecondary }]}>
                        {new Date(event.eventDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Text>
                    </View>
                    <View style={styles.completedBadge}>
                      <Text style={[styles.completedBadgeText, { color: colors.textSecondary }]}>Completed</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </>
        )}

        {/* Action Buttons */}
        <View style={[styles.actionSection, { backgroundColor: colors.background }]}>
          {/* Join/Leave Club Button - Only show for clubs, not for club owners viewing their own club */}
          {user?.accountType === 'CLUB' && currentUserDbId && user?.claimedBy !== currentUserDbId && (
            <TouchableOpacity
              style={[
                styles.membershipButton,
                { backgroundColor: isMember ? '#757575' : colors.primary },
                membershipLoading && styles.buttonDisabled,
              ]}
              onPress={isMember ? handleLeaveClub : handleJoinClub}
              disabled={membershipLoading}
            >
              {membershipLoading ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <>
                  <Icon
                    name={isMember ? 'person-remove' : 'person-add'}
                    size={20}
                    color="white"
                  />
                  <Text style={styles.membershipButtonText}>
                    {isMember ? 'Leave Club' : 'Join Club'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          )}

          {/* Friend Button */}
          {renderFriendButton()}
          
          {/* Send Message Button */}
          <TouchableOpacity style={[styles.messageButton, { backgroundColor: colors.primary }]} onPress={handleSendMessage}>
            <Icon name="chat" size={20} color="white" />
            <Text style={styles.messageButtonText}>Send Message</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  // Note: Most colors are now handled by dynamicStyles in the component
  // These static styles are kept for layout and structure only
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 18,
  },
  header: {
    padding: 20,
    alignItems: 'center',
    borderBottomWidth: 1,
  },
  backButton: {
    position: 'absolute',
    left: 16,
    top: 16,
    zIndex: 1,
  },
  profileImageContainer: {
    marginTop: 20,
    position: 'relative',
  },
  profileImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
  },
  profileImagePlaceholder: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  onlineIndicator: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 3,
    borderColor: 'white',
  },
  username: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 12,
    fontFamily: Platform.OS === 'web' ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' : undefined,
  },
  onlineStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  onlineStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  onlineStatusText: {
    fontSize: 13,
  },
  accountTypeBadge: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 20,
    marginTop: 8,
  },
  cityContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 8,
  },
  cityText: {
    fontSize: 14,
  },
  statusContainer: {
    marginTop: 10,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    maxWidth: '80%',
  },
  statusText: {
    fontSize: 14,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  accountTypeText: {
    fontSize: 14,
    fontWeight: '600',
  },
  section: {
    padding: 20,
    marginTop: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    fontFamily: Platform.OS === 'web' ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' : undefined,
  },
  statsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  statCard: {
    flex: 1,
    backgroundColor: '#f9f9f9',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 12,
    marginTop: 4,
  },
  platformStats: {
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
  },
  platformTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  ratingsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  ratingItem: {
    flex: 1,
    alignItems: 'center',
  },
  ratingLabel: {
    fontSize: 12,
  },
  ratingValue: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 4,
  },
  overallStats: {
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  overallLabel: {
    fontSize: 14,
    marginTop: 8,
  },
  overallValue: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 4,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoText: {
    fontSize: 16,
    color: '#666',
  },
  aboutText: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
  },
  websiteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  websiteText: {
    fontSize: 16,
    color: '#2E7D32',
    textDecorationLine: 'underline',
  },
  // Social links styles
  socialLinksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  socialLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  socialLinkText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  scheduleText: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
  },
  postCard: {
    backgroundColor: '#f9f9f9',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  postContent: {
    fontSize: 16,
    color: '#333',
    lineHeight: 22,
  },
  postDate: {
    fontSize: 12,
    color: '#999',
    marginTop: 8,
  },
  memberOnlyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
    marginBottom: 8,
    gap: 4,
  },
  memberOnlyBadgeText: {
    fontSize: 11,
    color: '#1976D2',
    fontWeight: '600',
  },
  actionSection: {
    padding: 20,
    paddingBottom: 40,
    gap: 12,
  },
  messageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  messageButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  membershipButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
    marginBottom: 12,
  },
  joinButton: {
    backgroundColor: '#1976D2',
  },
  leaveButton: {
    backgroundColor: '#757575',
  },
  membershipButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  memberCountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  memberCountInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  memberCountTextContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 6,
  },
  memberCountValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  memberCountLabel: {
    fontSize: 16,
    color: '#666',
  },
  memberBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    gap: 4,
  },
  memberBadgeText: {
    fontSize: 12,
    color: '#4CAF50',
    fontWeight: '600',
  },
  addFriendButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  friendButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  friendsButton: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 12,
    gap: 8,
  },
  friendsButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  pendingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  claimSection: {
    padding: 16,
    backgroundColor: '#FFF3E0',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FFB74D',
  },
  claimInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
    gap: 12,
  },
  claimTextContainer: {
    flex: 1,
  },
  claimTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  claimDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  claimButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#FF9800',
    padding: 14,
    borderRadius: 8,
  },
  claimButtonDisabled: {
    opacity: 0.6,
  },
  claimButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  // Event card styles
  eventCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    padding: 12,
    borderRadius: 12,
    marginBottom: 10,
  },
  eventCardContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  eventDateBadge: {
    width: 48,
    height: 48,
    borderRadius: 10,
    backgroundColor: '#E8F5E9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  eventDateDay: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2E7D32',
  },
  eventDateMonth: {
    fontSize: 11,
    color: '#2E7D32',
    textTransform: 'uppercase',
  },
  eventInfo: {
    flex: 1,
  },
  eventTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  eventMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  eventTypeTag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  eventTypeText: {
    fontSize: 11,
    color: '#2E7D32',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  eventRsvpCount: {
    fontSize: 12,
    color: '#666',
  },
  // History card styles (past events & tournaments)
  historyCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    padding: 14,
    borderRadius: 12,
    marginBottom: 10,
  },
  historyIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 10,
    backgroundColor: '#FFF8E1',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  historyInfo: {
    flex: 1,
  },
  historyTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  historyMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  historyMetaText: {
    fontSize: 12,
    color: '#666',
  },
  historyDate: {
    fontSize: 11,
    color: '#999',
  },
  completedBadge: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  completedBadgeText: {
    fontSize: 11,
    color: '#2E7D32',
    fontWeight: '600',
  },
  liveTodaySection: {
    backgroundColor: '#E8F5E9',
    borderLeftWidth: 4,
    borderLeftColor: '#4CAF50',
  },
  liveTodayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  liveDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#4CAF50',
  },
  liveTodayLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2E7D32',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  liveTodayTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  liveTodayRsvp: {
    fontSize: 14,
  },
  socialPostsScroll: {
    flexDirection: 'row',
    marginTop: 4,
  },
  socialPostCard: {
    width: 140,
    marginRight: 12,
    borderRadius: 12,
    overflow: 'hidden',
  },
  socialPostImage: {
    width: '100%',
    height: 140,
  },
  socialPostCaption: {
    fontSize: 11,
    padding: 8,
    lineHeight: 14,
  },
});

export default UserDetailScreen;

